import { ChangeEvent, useRef } from 'react';
import { toast } from 'react-toastify';
import trash from '../../../../../assets/images/icons/trash.svg';
import Button from '../../../../../components/Button';
import { MAX_PROGRAM_ATTACHMENTS, ProgramAttachmentItem } from '../../types';
import {
  AttachmentItem,
  AttachmentsContainer,
  AttachmentsHeader,
  AttachmentsList,
  HiddenInput,
} from './styles';

interface AttachmentsFieldProps {
  attachments: ProgramAttachmentItem[];
  onChange: (attachments: ProgramAttachmentItem[]) => void;
  disabled?: boolean;
}

export default function AttachmentsField({
  attachments,
  onChange,
  disabled = false,
}: AttachmentsFieldProps) {
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const remainingSlots = MAX_PROGRAM_ATTACHMENTS - attachments.length;

  function handleOpenFilePicker() {
    if (disabled || remainingSlots <= 0) {
      return;
    }
    hiddenFileInput.current?.click();
  }

  function handleFilesSelected(event: ChangeEvent<HTMLInputElement>) {
    const { files } = event.target;
    if (!files?.length) {
      return;
    }

    const selectedFiles = Array.from(files);
    if (selectedFiles.length > remainingSlots) {
      toast.warning(`É possível anexar no máximo ${MAX_PROGRAM_ATTACHMENTS} arquivos.`);
    }

    const filesToAdd = selectedFiles.slice(0, remainingSlots).map((file) => ({
      id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
      name: file.name,
      file,
    }));

    onChange([...attachments, ...filesToAdd]);
    event.target.value = '';
  }

  function handleRemoveAttachment(attachmentId: string) {
    onChange(attachments.filter((attachment) => attachment.id !== attachmentId));
  }

  return (
    <AttachmentsContainer>
      <AttachmentsHeader>
        <Button
          type="button"
          small
          onClick={handleOpenFilePicker}
          disabled={disabled || remainingSlots <= 0}
        >
          Adicionar anexos
        </Button>
        <small>
          {attachments.length}/{MAX_PROGRAM_ATTACHMENTS} arquivos
        </small>
      </AttachmentsHeader>

      <HiddenInput
        ref={hiddenFileInput}
        type="file"
        multiple
        onChange={handleFilesSelected}
        disabled={disabled || remainingSlots <= 0}
      />

      {attachments.length > 0 && (
        <AttachmentsList>
          {attachments.map((attachment) => (
            <AttachmentItem key={attachment.id}>
              {attachment.url ? (
                <a href={attachment.url} target="_blank" rel="noreferrer">
                  {attachment.name}
                </a>
              ) : (
                <span>{attachment.name}</span>
              )}
              <button
                type="button"
                onClick={() => handleRemoveAttachment(attachment.id)}
                disabled={disabled}
                title="Remover anexo"
              >
                <img src={trash} alt="Remover anexo" />
              </button>
            </AttachmentItem>
          ))}
        </AttachmentsList>
      )}
    </AttachmentsContainer>
  );
}
